import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const SaleForm = ({ isOpen, onClose, onSave, sale }) => {
  const [formData, setFormData] = useState({
    saleDate: new Date().toISOString().split('T')[0],
    vendorId: '',
    productId: '',
    quantity: 1,
    unitPrice: 0,
    remark: ''
  });
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    if (sale) {
      setFormData({
        saleDate: sale.sale_date || new Date().toISOString().split('T')[0],
        vendorId: sale.vendor_id || '',
        productId: sale.product_id || '',
        quantity: sale.quantity || 1,
        unitPrice: sale.unit_price || 0,
        remark: sale.remark || ''
      });
      setTotalPrice((sale.quantity || 1) * (sale.unit_price || 0));
    } else {
      setFormData({
        saleDate: new Date().toISOString().split('T')[0],
        vendorId: '',
        productId: '',
        quantity: 1,
        unitPrice: 0,
        remark: ''
      });
      setTotalPrice(0);
    }
  }, [sale]);

  const fetchMetadata = async () => {
    try {
      const [vendorsRes, productsRes] = await Promise.all([
        adminAPI.getVendors(),
        adminAPI.getProducts()
      ]);
      setVendors(vendorsRes.data.data || []);
      setProducts(productsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === 'quantity' || name === 'unitPrice' ? parseFloat(value) || 0 : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Calculate total price
    if (name === 'quantity' || name === 'unitPrice') {
      const qty = name === 'quantity' ? parseFloat(value) || 0 : formData.quantity;
      const price = name === 'unitPrice' ? parseFloat(value) || 0 : formData.unitPrice;
      setTotalPrice(qty * price);
    }
  };

  const handleProductChange = (e) => {
    const productId = parseInt(e.target.value);
    const product = products.find(p => p.id === productId);
    setFormData(prev => ({ 
      ...prev, 
      productId: productId,
      unitPrice: product?.base_price || 0
    }));
    setTotalPrice(formData.quantity * (product?.base_price || 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vendorId || !formData.productId || formData.quantity <= 0) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-xl font-semibold text-gray-900">
            {sale ? 'Edit Sale' : 'Record a Sale'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ti ti-x text-2xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sale Date *
            </label>
            <input
              type="date"
              name="saleDate"
              value={formData.saleDate}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor *
            </label>
            <select
              name="vendorId"
              value={formData.vendorId}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">Select Vendor</option>
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product *
            </label>
            <select
              name="productId"
              value={formData.productId}
              onChange={handleProductChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">Select Product</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.name} - ₹{product.base_price}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity *
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                min="1"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit Price (₹) *
              </label>
              <input
                type="number"
                name="unitPrice"
                value={formData.unitPrice}
                onChange={handleChange}
                min="0"
                step="0.01"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Total Price:</span>
              <span className="text-2xl font-bold text-teal-600">
                ₹{totalPrice.toLocaleString()}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remark
            </label>
            <textarea
              name="remark"
              value={formData.remark}
              onChange={handleChange}
              rows="2"
              placeholder="Optional notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : sale ? 'Update Sale' : 'Record Sale'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaleForm;